import React, { useEffect, useState, Children, memo, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { RoleWorkflowModal } from './RoleWorkflowModal';
import { PrdSectionNav, PrdExecutiveSummary, ExecutionGantt } from './PRDHelpers';
import { DatabaseIcon, BrainCircuitIcon, MonitorIcon, ArrowRightIcon, CheckCircle2Icon, AlertTriangleIcon, LayersIcon, WorkflowIcon, UsersIcon, TargetIcon, CalendarIcon, SparklesIcon, ShieldCheckIcon, CodeIcon, ServerIcon, CloudIcon, FileTextIcon, TrendingUpIcon, XIcon, UserCogIcon, BriefcaseIcon, ListChecksIcon, TableIcon, KeyIcon } from 'lucide-react';
const fadeUp = {
  initial: {
    opacity: 0,
    y: 24
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.6,
    ease: [0.22, 1, 0.36, 1]
  }
};
const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
export function PRDPage() {
  const [activeRoleId, setActiveRoleId] = useState<string | null>(null);
  return <>
      <div className="min-h-screen bg-white" data-id="element-5930">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100" data-id="element-5931">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between" data-id="element-5932">
            <Link to="/" className="flex items-center gap-2" data-id="element-5933">
              <div className="font-bold text-lg flex items-start leading-none" data-id="element-5934">
                <span className="text-brand-navy" data-id="element-5935">Offsite</span>
                <span className="text-brand-coral font-black relative -top-0.5 ml-0.5" data-id="element-5936">
                  +
                </span>
              </div>
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-3 hidden sm:inline" data-id="element-5937">
                · PRD
              </span>
            </Link>
            <Link to="/" className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors" data-id="element-5938">
              <XIcon className="w-4 h-4 text-slate-600" data-id="element-5939" />
            </Link>
          </div>
          {/* Section TOC */}
          <PrdSectionNav data-id="element-5940" />
        </header>

        {/* Hero */}
        <section className="py-20 px-6 md:px-12 bg-gradient-to-br from-slate-50 to-white border-b border-slate-100" data-id="element-5941">
          <div className="max-w-5xl mx-auto" data-id="element-5942">
            <motion.div variants={stagger} initial="initial" animate="animate" className="text-center" data-id="element-5943">
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-teal/10 text-brand-teal text-sm font-bold mb-6" data-id="element-5944">
                <FileTextIcon className="w-4 h-4" data-id="element-5945" />
                Product Requirements Document
              </motion.div>
              <motion.h1 variants={fadeUp} className="font-serif-display text-5xl md:text-6xl font-bold text-brand-navy mb-6 leading-tight" data-id="element-5946">
                Offsite+ Architecture & Product Spec
              </motion.h1>
              <motion.p variants={fadeUp} className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto leading-relaxed" data-id="element-5947">
                A comprehensive technical and product blueprint for the
                AI-powered event intelligence layer that powers every type of
                hotel event — from corporate meetings and conferences to
                weddings, birthdays, sports teams, and community celebrations.
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-3 text-sm" data-id="element-5948">
                <div className="px-4 py-2 rounded-lg bg-white border border-slate-200 font-semibold text-slate-700" data-id="element-5949">
                  Next.js 14
                </div>
                <div className="px-4 py-2 rounded-lg bg-white border border-slate-200 font-semibold text-slate-700" data-id="element-5950">
                  Sanity CMS
                </div>
                <div className="px-4 py-2 rounded-lg bg-white border border-slate-200 font-semibold text-slate-700" data-id="element-5951">
                  NotebookLM
                </div>
                <div className="px-4 py-2 rounded-lg bg-white border border-slate-200 font-semibold text-slate-700" data-id="element-5952">
                  Supabase
                </div>
                <div className="px-4 py-2 rounded-lg bg-white border border-slate-200 font-semibold text-slate-700" data-id="element-5953">
                  Vercel
                </div>
                <div className="px-4 py-2 rounded-lg bg-white border border-slate-200 font-semibold text-slate-700 inline-flex items-center gap-1.5" data-id="element-5954">
                  <span className="w-1.5 h-1.5 rounded-full bg-pink-500" data-id="element-5955" />
                  Mux Video
                </div>
                <div className="px-4 py-2 rounded-lg bg-white border border-slate-200 font-semibold text-slate-700" data-id="element-5956">
                  Claude Code + Cursor
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Product Promise */}
        <section id="promise" className="py-16 px-6 md:px-12 border-b border-slate-100 scroll-mt-32" data-id="element-5957">
          <div className="max-w-5xl mx-auto" data-id="element-5958">
            <div className="bg-gradient-to-br from-brand-navy to-brand-navy/90 rounded-3xl p-10 md:p-16 text-white relative overflow-hidden" data-id="element-5959">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,165,164,0.15),transparent_50%)] pointer-events-none" data-id="element-5960" />
              <div className="relative z-10" data-id="element-5961">
                <div className="text-sm font-bold text-brand-teal uppercase tracking-wider mb-4" data-id="element-5962">
                  Product Promise
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl font-bold mb-6 leading-tight" data-id="element-5963">
                  "Every hotel event becomes a smarter experience — before,
                  during, and after the booking."
                </h2>
                <p className="text-lg text-slate-300 leading-relaxed" data-id="element-5964">
                  Hotels already provide the room, catering, and service.
                  Offsite+ adds the missing digital and experience layer for
                  every event type a hotel hosts — corporate meetings,
                  conferences, retreats, training days, weddings, birthdays,
                  sports teams, and community events.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TL;DR / Executive Summary */}
        <PrdExecutiveSummary data-id="element-5965" />

        {/* Foundations Header */}
        <section id="foundations" className="py-12 px-6 md:px-12 border-b border-slate-100 scroll-mt-32" data-id="element-5966">
          <div className="max-w-5xl mx-auto text-center" data-id="element-5967">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 text-amber-700 text-xs font-bold uppercase tracking-wider mb-4" data-id="element-5968">
              <KeyIcon className="w-3.5 h-3.5" data-id="element-5969" />
              Foundations · Before AI
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl font-bold text-brand-navy mb-4 leading-tight" data-id="element-5970">
              Define the boring tables before the smart layer.
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed" data-id="element-5971">
              Offsite+ is an <strong data-id="element-5972">Event Intelligence Platform</strong> for
              any event a hotel hosts. Many social events (birthdays, showers,
              family celebrations) are free — we organize their thoughts and the
              hotel looks good.{' '}
              <strong data-id="element-5973">Revenue comes from the corporate lane.</strong> Before
              building AI, lock the four foundations in priority order: user
              roles, core jobs, business rules, and the operational data model.
            </p>
          </div>
        </section>

        {/* Build Order Callout */}
        <section className="px-6 md:px-12 -mt-2 mb-2" data-id="element-5974">
          <div className="max-w-5xl mx-auto" data-id="element-5975">
            <div className="bg-gradient-to-br from-brand-navy to-brand-navy/90 text-white rounded-2xl p-8 md:p-10 relative overflow-hidden" data-id="element-5976">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,165,164,0.15),transparent_60%)] pointer-events-none" data-id="element-5977" />
              <div className="relative z-10" data-id="element-5978">
                <div className="flex items-center gap-2 text-brand-teal text-xs font-bold uppercase tracking-wider mb-3" data-id="element-5979">
                  <WorkflowIcon className="w-4 h-4" data-id="element-5980" />
                  Build Order · Workflow #1 First
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl font-bold mb-4 leading-tight" data-id="element-5981">
                  Start with the Hotel Sales Manager. Ship that workflow before
                  anything else.
                </h3>
                <p className="text-slate-300 leading-relaxed mb-6" data-id="element-5982">
                  The first workflow we build is the Sales Manager uploading
                  their <strong className="text-white" data-id="element-5983">book of business</strong>{' '}
                  (PDFs, spreadsheets, booking data) and turning each booking
                  into a tagged event with a recommended package. Every other
                  role and job is built in priority order behind this one.
                </p>
                <div className="grid sm:grid-cols-2 gap-4" data-id="element-5984">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-id="element-5985">
                    <div className="text-xs font-bold text-brand-teal uppercase tracking-wider mb-2" data-id="element-5986">
                      Now · P1
                    </div>
                    <div className="text-sm font-bold text-white mb-1" data-id="element-5987">
                      Sales Manager uploads book of business
                    </div>
                    <div className="text-xs text-slate-400" data-id="element-5988">
                      PDFs → events → packages → proposals
                    </div>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-id="element-5989">
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-5990">
                      Next · P2–P4
                    </div>
                    <div className="text-sm font-bold text-white mb-1" data-id="element-5991">
                      Coordinator → Client → Attendee
                    </div>
                    <div className="text-xs text-slate-400" data-id="element-5992">
                      Operational hub, then guest experience
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 1. User Roles */}
        <section id="roles" className="py-20 px-6 md:px-12 bg-slate-50 scroll-mt-32" data-id="element-5993">
          <div className="max-w-6xl mx-auto" data-id="element-5994">
            <div className="flex items-start gap-4 mb-12" data-id="element-5995">
              <div className="w-12 h-12 rounded-xl bg-brand-navy text-white flex items-center justify-center shrink-0 font-bold text-lg" data-id="element-5996">
                1
              </div>
              <div data-id="element-5997">
                <div className="flex items-center gap-2 text-brand-teal text-xs font-bold uppercase tracking-wider mb-2" data-id="element-5998">
                  <UserCogIcon className="w-4 h-4" data-id="element-5999" />
                  User Roles · In Build Priority Order
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl font-bold text-brand-navy mb-3" data-id="element-6000">
                  Who logs in — ranked by what we build first.
                </h2>
                <p className="text-lg text-slate-600 max-w-3xl" data-id="element-6001">
                  Roles 1–4 are the priority lanes we ship now. Roles 5–6 follow
                  once the core workflow is solid. Roles 7–9 are needed
                  eventually but not blocking. The same role plays the same part
                  whether the event is a board offsite, a wedding, or a kids'
                  birthday.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-6002">
              {[{
              priority: 1,
              tier: 'P1 · Build First',
              tierColor: 'bg-brand-coral text-white',
              role: 'Hotel Sales Manager',
              scope: 'Property',
              desc: 'The starting point. Uploads the book of business (PDFs, spreadsheets), tags bookings as events, picks packages, generates proposals, owns the client relationship.',
              color: 'bg-brand-coral/10 text-brand-coral'
            }, {
              priority: 2,
              tier: 'P2 · Build Next',
              tierColor: 'bg-brand-teal text-white',
              role: 'Event Coordinator',
              scope: 'Event',
              desc: 'Operational owner of one event — agenda, materials, vendor coordination, on-site execution. Often the handoff from Sales Manager.',
              color: 'bg-brand-teal/10 text-brand-teal'
            }, {
              priority: 3,
              tier: 'P3 · Build Next',
              tierColor: 'bg-brand-teal text-white',
              role: 'Client / Event Organizer',
              scope: 'Event',
              desc: 'External buyer (corporate planner, bride, parent, association). Often the same person as the Event Coordinator on smaller events.',
              color: 'bg-purple-50 text-purple-700'
            }, {
              priority: 4,
              tier: 'P4 · Build Next',
              tierColor: 'bg-brand-teal text-white',
              role: 'Attendee / Guest',
              scope: 'Event',
              desc: 'End user of the digital event hub — sees agenda, materials, photos, surveys, follow-up. Free for social events, the experience layer for corporate.',
              color: 'bg-amber-50 text-amber-700'
            }, {
              priority: 5,
              tier: 'P5 · Phase 2',
              tierColor: 'bg-slate-600 text-white',
              role: 'Hotel Admin / Owner',
              scope: 'Property',
              desc: 'Configures the property, manages staff accounts, sets package pricing, sees revenue across all events.',
              color: 'bg-blue-50 text-blue-700'
            }, {
              priority: 6,
              tier: 'P6 · Phase 2',
              tierColor: 'bg-slate-600 text-white',
              role: 'Platform Admin',
              scope: 'Offsite+',
              desc: 'Manages the multi-tenant platform, hotel onboarding, billing, and global content templates.',
              color: 'bg-brand-navy text-white'
            }, {
              priority: 7,
              tier: 'P7 · Later',
              tierColor: 'bg-slate-200 text-slate-600',
              role: 'Facilitator / Host',
              scope: 'Event',
              desc: 'Leads sessions or activities (workshop facilitator, MC, coach, speaker). Important for premium corporate lane but not blocking.',
              color: 'bg-emerald-50 text-emerald-700'
            }, {
              priority: 8,
              tier: 'P8 · Later',
              tierColor: 'bg-slate-200 text-slate-600',
              role: 'Sponsor / Vendor',
              scope: 'Event',
              desc: 'Conference sponsors, wedding vendors, donors. Limited access to their own page and lead capture.',
              color: 'bg-rose-50 text-rose-700'
            }, {
              priority: 9,
              tier: 'P9 · Later',
              tierColor: 'bg-slate-200 text-slate-600',
              role: 'Read-Only Viewer',
              scope: 'Event or Property',
              desc: 'Stakeholders (parents, executives, board members) granted view access to specific hubs or reports.',
              color: 'bg-slate-100 text-slate-700'
            }].map((r, i) => <div key={i} className={`bg-white rounded-2xl border-2 p-5 hover:shadow-md transition-shadow relative ${r.priority <= 4 ? 'border-brand-coral/30' : r.priority <= 6 ? 'border-slate-300' : 'border-slate-200 opacity-90'}`} data-id="element-6003">
                  <div className="absolute -top-2 -left-2 w-8 h-8 rounded-full bg-brand-navy text-white text-sm font-black flex items-center justify-center shadow-md" data-id="element-6004">
                    {r.priority}
                  </div>
                  <div className="flex items-center justify-between mb-3 pl-7" data-id="element-6005">
                    <div className={`w-9 h-9 rounded-lg ${r.color} flex items-center justify-center`} data-id="element-6006">
                      <UsersIcon className="w-4 h-4" data-id="element-6007" />
                    </div>
                    <div className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${r.tierColor}`} data-id="element-6008">
                      {r.tier}
                    </div>
                  </div>
                  <h3 className="text-base font-bold text-brand-navy mb-1" data-id="element-6009">
                    {r.role}
                  </h3>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2" data-id="element-6010">
                    Scope · {r.scope}
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4" data-id="element-6011">
                    {r.desc}
                  </p>
                  <button type="button" onClick={() => setActiveRoleId(`p${r.priority}`)} className="w-full mt-auto inline-flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-brand-navy text-white text-xs font-bold hover:bg-brand-navy/90 transition-colors group" data-id="element-6012">
                    <span className="inline-flex items-center gap-1.5" data-id="element-6013">
                      <SparklesIcon className="w-3.5 h-3.5" data-id="element-6014" />
                      View Workflow UI
                    </span>
                    <ArrowRightIcon className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" data-id="element-6015" />
                  </button>
                </div>)}
            </div>

            <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500" data-id="element-6016">
              <SparklesIcon className="w-4 h-4 text-brand-coral" data-id="element-6017" />
              <span data-id="element-6018">
                Click any role to open a full UI walk-through with tutorial
                bubbles, database impact, and what happens after each screen.
              </span>
            </div>
          </div>
        </section>

        {/* 2. Core Jobs */}
        <section id="jobs" className="py-20 px-6 md:px-12 border-b border-slate-100 scroll-mt-32" data-id="element-6019">
          <div className="max-w-6xl mx-auto" data-id="element-6020">
            <div className="flex items-start gap-4 mb-12" data-id="element-6021">
              <div className="w-12 h-12 rounded-xl bg-brand-navy text-white flex items-center justify-center shrink-0 font-bold text-lg" data-id="element-6022">
                2
              </div>
              <div data-id="element-6023">
                <div className="flex items-center gap-2 text-brand-coral text-xs font-bold uppercase tracking-wider mb-2" data-id="element-6024">
                  <BriefcaseIcon className="w-4 h-4" data-id="element-6025" />
                  Core Jobs · In Build Priority Order
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl font-bold text-brand-navy mb-3" data-id="element-6026">
                  Each job ships as its own workflow.
                </h2>
                <p className="text-lg text-slate-600 max-w-3xl" data-id="element-6027">
                  We build one workflow at a time, in this order. The starred
                  job at the top is the very first thing we ship — Sales Manager
                  uploads the book of business and converts it into tagged
                  events with recommended packages.
                </p>
              </div>
            </div>

            <div className="space-y-4" data-id="element-6028">
              {[{
              priority: 1,
              role: 'Hotel Sales Manager',
              color: 'bg-brand-coral',
              lane: 'Premium money lane',
              starJobIndex: 0,
              jobs: ['Upload the book of business — PDFs, spreadsheets, and existing booking data', 'Tag a confirmed booking with an event type', 'Pick (or accept the recommended) event package', 'Generate a client-ready proposal in one click', 'Hand the event off to a coordinator without re-explaining', 'See pipeline value and rebooking opportunities']
            }, {
              priority: 2,
              role: 'Event Coordinator',
              color: 'bg-brand-teal',
              lane: 'Operational backbone',
              jobs: ['Build or copy an event agenda from a template', 'Upload materials, vendor info, and venue details', 'Invite attendees and track RSVPs', 'Assign rooms, meals, and pods/tables', 'Run the event-day checklist and resolve issues']
            }, {
              priority: 3,
              role: 'Client / Event Organizer',
              color: 'bg-purple-500',
              lane: 'Often = the Coordinator on small events',
              jobs: ['Brand and customize the event hub', 'Add the guest list and tier permissions', 'Approve the agenda and any AI-suggested content', 'Communicate updates and changes to attendees', 'Review the post-event report and request follow-up']
            }, {
              priority: 4,
              role: 'Attendee / Guest',
              color: 'bg-amber-500',
              lane: 'Free for social events, value layer for corporate',
              jobs: ['Find the event hub via a single link or QR code', 'See what is happening now and what is next', 'Access materials, directions, dress code, and FAQs', 'Upload photos, complete surveys, and RSVP to side events', 'Receive recap, thank-you, and follow-up after the event']
            }, {
              priority: 5,
              role: 'Hotel Admin / Owner',
              color: 'bg-blue-500',
              lane: 'Phase 2',
              jobs: ['See revenue, attach-rate, and rebooking across all events', 'Compare event types and packages by margin', 'Manage staff accounts and permissions', 'Configure property-wide branding and pricing rules']
            }, {
              priority: 6,
              role: 'Platform Admin',
              color: 'bg-brand-navy',
              lane: 'Phase 2 · Internal',
              jobs: ['Onboard new hotel tenants', 'Manage global content templates and brand kits', 'Monitor platform-wide usage and billing', 'Approve revenue splits and refund exceptions']
            }, {
              priority: 7,
              role: 'Facilitator / Host',
              color: 'bg-emerald-500',
              lane: 'Later · Premium corporate lane',
              jobs: ['See pre-event intake and participant context', 'Run live polls, prompts, and group activities', 'Capture session notes and outcomes', 'Review attendee feedback in real time']
            }, {
              priority: 8,
              role: 'Sponsor / Vendor',
              color: 'bg-rose-500',
              lane: 'Later · Conference & wedding revenue',
              jobs: ['Edit their own sponsor / vendor page', 'Capture leads and meeting requests', 'See basic engagement analytics']
            }, {
              priority: 9,
              role: 'Read-Only Viewer',
              color: 'bg-slate-400',
              lane: 'Later · Stakeholder access',
              jobs: ['View a single event hub via a granted link', 'View aggregated reports without edit access']
            }].map((group, i) => <div key={i} className={`bg-white rounded-2xl border p-6 ${group.priority <= 4 ? 'border-brand-coral/30 shadow-sm' : 'border-slate-200'}`} data-id="element-6029">
                  <div className="flex items-center gap-3 mb-4 flex-wrap" data-id="element-6030">
                    <div className={`w-2 h-8 rounded-full ${group.color}`} data-id="element-6031" />
                    <div className="w-7 h-7 rounded-full bg-brand-navy text-white text-xs font-black flex items-center justify-center shrink-0" data-id="element-6032">
                      {group.priority}
                    </div>
                    <h3 className="text-lg font-bold text-brand-navy" data-id="element-6033">
                      {group.role}
                    </h3>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-slate-100 text-slate-600" data-id="element-6034">
                      {group.lane}
                    </span>
                  </div>
                  <ul className="grid md:grid-cols-2 gap-x-6 gap-y-2" data-id="element-6035">
                    {group.jobs.map((job, j) => <li key={j} className={`flex items-start gap-2 text-sm ${group.starJobIndex === j ? 'text-brand-navy font-semibold' : 'text-slate-700'}`} data-id="element-6036">
                        {group.starJobIndex === j ? <span className="text-brand-coral shrink-0 mt-0.5 text-base leading-none" data-id="element-6037">
                            ★
                          </span> : <CheckCircle2Icon className="w-4 h-4 text-brand-teal shrink-0 mt-0.5" data-id="element-6038" />}
                        <span data-id="element-6039">{job}</span>
                      </li>)}
                  </ul>
                </div>)}
            </div>

            <div className="mt-8 bg-brand-coral/5 border border-brand-coral/20 rounded-xl p-6 max-w-4xl mx-auto" data-id="element-6040">
              <div className="flex items-start gap-3" data-id="element-6041">
                <SparklesIcon className="w-5 h-5 text-brand-coral shrink-0 mt-0.5" data-id="element-6042" />
                <div data-id="element-6043">
                  <div className="font-bold text-brand-navy mb-1" data-id="element-6044">
                    ★ Workflow #1: The Book of Business Upload
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed" data-id="element-6045">
                    Sales Manager drops in PDFs and CSVs from their existing
                    book of business. Offsite+ parses each booking, detects the
                    event type, recommends a package, and generates a
                    client-ready proposal.{' '}
                    <strong data-id="element-6046">This is the very first feature we ship.</strong>{' '}
                    Everything else builds on it.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 3. Business Rules */}
        <section id="rules" className="py-20 px-6 md:px-12 bg-slate-50 scroll-mt-32" data-id="element-6047">
          <div className="max-w-6xl mx-auto" data-id="element-6048">
            <div className="flex items-start gap-4 mb-12" data-id="element-6049">
              <div className="w-12 h-12 rounded-xl bg-brand-navy text-white flex items-center justify-center shrink-0 font-bold text-lg" data-id="element-6050">
                3
              </div>
              <div data-id="element-6051">
                <div className="flex items-center gap-2 text-purple-700 text-xs font-bold uppercase tracking-wider mb-2" data-id="element-6052">
                  <ListChecksIcon className="w-4 h-4" data-id="element-6053" />
                  Business Rules
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl font-bold text-brand-navy mb-3" data-id="element-6054">
                  The logic that governs the system.
                </h2>
                <p className="text-lg text-slate-600 max-w-3xl" data-id="element-6055">
                  Rules apply consistently across every event type. They define
                  what is allowed, what is required, and what happens
                  automatically.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4" data-id="element-6056">
              {[{
              category: 'Tenancy & Access',
              rules: ['Every record belongs to exactly one Hotel (tenant).', 'Users only see events from properties they are assigned to.', 'Attendees can only access events they are invited to.', 'Sponsors only see their own page and lead data.']
            }, {
              category: 'Events & Packages',
              rules: ['Every event has exactly one event type (corporate, wedding, birthday, conference, training, sports, community, social).', 'Each event must have one assigned package (Basic, Lite, Smart Upgrade, or Premium).', 'A package determines which features and modules are available on the hub.', 'An event has one Sales Manager and one Coordinator (can be the same person).']
            }, {
              category: 'Lifecycle · Confirmed by Alex',
              rules: ['Events transition through 5 states: Draft → Confirmed → Live → Completed → Archived.', 'A hub goes live no earlier than the configured pre-event window (default 14 days).', 'After Completed, the hub stays accessible to attendees for 90 days unless extended.', 'Archived events are read-only and excluded from active dashboards.']
            }, {
              category: 'Content & Approval',
              rules: ['AI-generated content always lands in Draft status.', 'A human (Coordinator or Client) must approve content before it appears on the hub.', 'All content is versioned; rollback is always available.', 'Brand-approved templates cannot be deleted, only superseded.']
            }, {
              category: 'Billing & Revenue',
              rules: ['Package price is locked at event creation and stored on the event record.', 'Add-ons are line items on the event invoice.', 'Hotel collects payment; Offsite+ holds the money first and settles with the hotel later (deferred revenue split — see open decisions).', 'Refunds and cancellations require Hotel Admin approval.']
            }, {
              category: 'Notifications & Follow-up',
              rules: ['Pre-event reminders fire on a schedule defined by the package.', 'Post-event recap and review prompt are sent within 48 hours of completion.', 'Rebooking offers are scheduled 30, 60, and 90 days post-event.', 'Attendees can opt out of any non-essential message at any time.']
            }].map((group, i) => <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6" data-id="element-6057">
                  <div className="flex items-center gap-2 mb-4 pb-4 border-b border-slate-100" data-id="element-6058">
                    <ShieldCheckIcon className="w-4 h-4 text-brand-teal" data-id="element-6059" />
                    <h3 className="text-base font-bold text-brand-navy" data-id="element-6060">
                      {group.category}
                    </h3>
                  </div>
                  <ul className="space-y-2.5" data-id="element-6061">
                    {group.rules.map((rule, j) => <li key={j} className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed" data-id="element-6062">
                        <div className="w-1 h-1 rounded-full bg-slate-400 shrink-0 mt-2" data-id="element-6063" />
                        <span data-id="element-6064">{rule}</span>
                      </li>)}
                  </ul>
                </div>)}
            </div>

            {/* Open Decisions */}
            <div className="mt-10 max-w-5xl mx-auto" data-id="element-6065">
              <div className="bg-white border-2 border-dashed border-amber-300 rounded-2xl p-6 md:p-8" data-id="element-6066">
                <div className="flex items-center gap-2 text-amber-700 text-xs font-bold uppercase tracking-wider mb-4" data-id="element-6067">
                  <AlertTriangleIcon className="w-4 h-4" data-id="element-6068" />
                  Open Decisions · Need a Call
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-5" data-id="element-6069">
                  Three items flagged for Ash and Alex to lock.
                </h3>
                <div className="space-y-4" data-id="element-6070">
                  <div className="flex items-start gap-3 p-4 bg-amber-50/50 rounded-xl" data-id="element-6071">
                    <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-700 font-bold text-sm flex items-center justify-center shrink-0" data-id="element-6072">
                      1
                    </div>
                    <div className="flex-1" data-id="element-6073">
                      <div className="font-bold text-brand-navy mb-1 text-sm" data-id="element-6074">
                        Pre-event window (14 days) — configurable per package,
                        or fixed platform rule?
                      </div>
                      <div className="text-sm text-slate-600" data-id="element-6075">
                        Alex raised this. Premium packages may want a longer
                        window for pre-work; Lite packages may want shorter.
                        Leaning <strong data-id="element-6076">configurable per package</strong> with a
                        platform default.
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-amber-50/50 rounded-xl" data-id="element-6077">
                    <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-700 font-bold text-sm flex items-center justify-center shrink-0" data-id="element-6078">
                      2
                    </div>
                    <div className="flex-1" data-id="element-6079">
                      <div className="font-bold text-brand-navy mb-1 text-sm" data-id="element-6080">
                        Post-event hub access (90 days) — configurable per
                        package, or fixed?
                      </div>
                      <div className="text-sm text-slate-600" data-id="element-6081">
                        Same logic. Premium clients may want a year of access
                        for follow-up; social events may sunset after 30 days.
                        Leaning <strong data-id="element-6082">configurable per package</strong>.
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-amber-50/50 rounded-xl" data-id="element-6083">
                    <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-700 font-bold text-sm flex items-center justify-center shrink-0" data-id="element-6084">
                      3
                    </div>
                    <div className="flex-1" data-id="element-6085">
                      <div className="font-bold text-brand-navy mb-1 text-sm" data-id="element-6086">
                        Revenue split with the hotel — day one, or later?
                      </div>
                      <div className="text-sm text-slate-600" data-id="element-6087">
                        <strong data-id="element-6088">Decision: defer.</strong> Offsite+ collects
                        payment first, settles with the hotel later. Get the
                        money in, control the float, work out the split in a
                        later phase.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4. Data Model */}
        <section id="data" className="py-20 px-6 md:px-12 border-b border-slate-100 scroll-mt-32" data-id="element-6089">
          <div className="max-w-6xl mx-auto" data-id="element-6090">
            <div className="flex items-start gap-4 mb-12" data-id="element-6091">
              <div className="w-12 h-12 rounded-xl bg-brand-navy text-white flex items-center justify-center shrink-0 font-bold text-lg" data-id="element-6092">
                4
              </div>
              <div data-id="element-6093">
                <div className="flex items-center gap-2 text-amber-700 text-xs font-bold uppercase tracking-wider mb-2" data-id="element-6094">
                  <TableIcon className="w-4 h-4" data-id="element-6095" />
                  Data Model · The Boring Tables
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl font-bold text-brand-navy mb-3" data-id="element-6096">
                  Define the relational core before adding AI.
                </h2>
                <p className="text-lg text-slate-600 max-w-3xl" data-id="element-6097">
                  These tables live in Supabase / Postgres. They are the
                  operational system of record — bookings, users, agendas,
                  payments — independent of any AI or content layer.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-6098">
              {[{
              table: 'hotels',
              purpose: 'The tenant. One row per property.',
              fields: ['id (uuid)', 'name', 'slug', 'brand_group_id', 'timezone', 'currency', 'created_at']
            }, {
              table: 'users',
              purpose: 'All authenticated humans.',
              fields: ['id (uuid)', 'email', 'full_name', 'phone', 'avatar_url', 'created_at', 'last_seen_at']
            }, {
              table: 'memberships',
              purpose: 'Maps users to hotels with a role.',
              fields: ['id', 'user_id → users', 'hotel_id → hotels', 'role (enum)', 'status', 'invited_by']
            }, {
              table: 'clients',
              purpose: 'External event buyers (companies, families, organizers).',
              fields: ['id', 'hotel_id → hotels', 'name', 'type (corporate / individual / nonprofit)', 'primary_contact_user_id', 'address']
            }, {
              table: 'events',
              purpose: 'The central object. One row per event.',
              fields: ['id', 'hotel_id → hotels', 'client_id → clients', 'event_type (enum)', 'package_id → packages', 'status (enum)', 'starts_at, ends_at', 'sales_manager_id → users', 'coordinator_id → users']
            }, {
              table: 'packages',
              purpose: 'Reusable event package templates with pricing.',
              fields: ['id', 'hotel_id → hotels', 'name', 'tier (basic / lite / smart / premium)', 'base_price', 'feature_flags (jsonb)']
            }, {
              table: 'venues',
              purpose: 'Physical spaces inside a hotel.',
              fields: ['id', 'hotel_id → hotels', 'name', 'capacity', 'type (ballroom / conf / outdoor)']
            }, {
              table: 'event_venues',
              purpose: 'Many-to-many: which venues are used for an event.',
              fields: ['event_id → events', 'venue_id → venues', 'starts_at, ends_at', 'setup_notes']
            }, {
              table: 'agenda_items',
              purpose: 'Schedule blocks for an event.',
              fields: ['id', 'event_id → events', 'title', 'description', 'starts_at, ends_at', 'location', 'host_user_id → users']
            }, {
              table: 'attendees',
              purpose: 'Guests / participants for an event.',
              fields: ['id', 'event_id → events', 'user_id → users (nullable)', 'name', 'email', 'rsvp_status', 'tier (vip / standard / parent)']
            }, {
              table: 'materials',
              purpose: 'Files, links, and resources attached to an event.',
              fields: ['id', 'event_id → events', 'kind (file / link / video)', 'url', 'visibility (public / attendees / staff)']
            }, {
              table: 'messages',
              purpose: 'Outbound communications log.',
              fields: ['id', 'event_id → events', 'channel (email / sms / push)', 'template_id', 'status', 'sent_at']
            }, {
              table: 'surveys & responses',
              purpose: 'Feedback before, during, after the event.',
              fields: ['survey_id, event_id, audience', 'response_id, attendee_id, answers (jsonb)']
            }, {
              table: 'invoices & line_items',
              purpose: 'Billing for the event and add-ons.',
              fields: ['invoice_id, event_id, total, status', 'line_item_id, description, amount']
            }, {
              table: 'audit_log',
              purpose: 'Every meaningful change. Immutable.',
              fields: ['id', 'actor_user_id', 'entity_type, entity_id', 'action', 'diff (jsonb)', 'created_at']
            }].map((t, i) => <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 hover:border-brand-teal/40 transition-colors" data-id="element-6099">
                  <div className="flex items-center gap-2 mb-2" data-id="element-6100">
                    <TableIcon className="w-4 h-4 text-brand-teal" data-id="element-6101" />
                    <code className="text-sm font-bold font-mono text-brand-navy" data-id="element-6102">
                      {t.table}
                    </code>
                  </div>
                  <p className="text-xs text-slate-500 mb-3 italic" data-id="element-6103">
                    {t.purpose}
                  </p>
                  <div className="space-y-1" data-id="element-6104">
                    {t.fields.map((field, j) => <div key={j} className="text-[11px] font-mono text-slate-700 bg-slate-50 px-2 py-1 rounded border border-slate-100" data-id="element-6105">
                        {field}
                      </div>)}
                  </div>
                </div>)}
            </div>

            <div className="mt-10 bg-amber-50 border border-amber-200 rounded-xl p-6 max-w-4xl mx-auto" data-id="element-6106">
              <div className="flex items-start gap-3" data-id="element-6107">
                <AlertTriangleIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" data-id="element-6108" />
                <div data-id="element-6109">
                  <div className="font-bold text-amber-900 mb-1" data-id="element-6110">
                    Order of operations
                  </div>
                  <p className="text-sm text-amber-800 leading-relaxed" data-id="element-6111">
                    These tables ship <strong data-id="element-6112">before</strong> any AI feature.
                    Once the operational core is solid, the AI/content layer
                    below (Sanity + NotebookLM) attaches to{' '}
                    <code className="font-mono text-xs bg-white px-1 py-0.5 rounded" data-id="element-6113">
                      events
                    </code>
                    ,{' '}
                    <code className="font-mono text-xs bg-white px-1 py-0.5 rounded" data-id="element-6114">
                      attendees
                    </code>
                    , and{' '}
                    <code className="font-mono text-xs bg-white px-1 py-0.5 rounded" data-id="element-6115">
                      agenda_items
                    </code>{' '}
                    — never the other way around.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Execution Gantt */}
        <div id="execution" className="scroll-mt-32" data-id="element-6116">
          <ExecutionGantt data-id="element-6117" />
        </div>

        {/* AI/Content Architecture Header */}
        <section id="ai-layer" className="py-12 px-6 md:px-12 bg-gradient-to-b from-white to-slate-50 scroll-mt-32" data-id="element-6118">
          <div className="max-w-5xl mx-auto text-center" data-id="element-6119">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-teal/10 text-brand-teal text-xs font-bold uppercase tracking-wider mb-4" data-id="element-6120">
              <SparklesIcon className="w-3.5 h-3.5" data-id="element-6121" />
              Now · The Smart Layer
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl font-bold text-brand-navy mb-4 leading-tight" data-id="element-6122">
              With the foundations locked, layer in the intelligence.
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed" data-id="element-6123">
              The AI and content systems below sit on top of the boring tables —
              personalizing experiences, generating copy, and automating
              follow-up.
            </p>
          </div>
        </section>

        {/* 3-Layer Architecture */}
        <section className="py-20 px-6 md:px-12 bg-slate-50" data-id="element-6124">
          <div className="max-w-6xl mx-auto" data-id="element-6125">
            <div className="text-center mb-12" data-id="element-6126">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-teal/10 text-brand-teal text-xs font-bold uppercase tracking-wider mb-4" data-id="element-6127">
                <LayersIcon className="w-3.5 h-3.5" data-id="element-6128" />
                How the Smart Layer is Built
              </div>
              <h2 className="font-serif-display text-4xl font-bold text-brand-navy mb-4" data-id="element-6129">
                The 3-Layer Architecture
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-6130">
                NotebookLM generates intelligence. Sanity structures content.
                Offsite+ delivers personalized experiences — across every event
                type a hotel hosts.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6" data-id="element-6131">
              {[{
              icon: BrainCircuitIcon,
              title: 'NotebookLM',
              subtitle: 'Intelligence / Synthesis Layer',
              color: 'bg-amber-500',
              items: ['Generate summaries', 'Create learning paths', 'Source-based briefs', 'Facilitator notes', 'Post-session recaps', 'Personalized outputs'],
              note: 'NOT the content database'
            }, {
              icon: DatabaseIcon,
              title: 'Sanity',
              subtitle: 'Structured Content System of Record',
              color: 'bg-brand-teal',
              items: ['Canonical content', 'Approved modules', 'Structured objects', 'Versioning', 'Multi-language', 'Brand-approved media'],
              note: 'The clean publishing backbone'
            }, {
              icon: MonitorIcon,
              title: 'Offsite+',
              subtitle: 'Delivery + Personalization Layer',
              color: 'bg-brand-coral',
              items: ['Client portals', 'Hotel hubs', 'Participant apps', 'PDF proposals', 'Email sequences', 'Live dashboards'],
              note: 'Where users experience it'
            }].map((layer, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.15,
              duration: 0.6
            }} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-6132">
                  <div className={`w-12 h-12 rounded-xl ${layer.color} flex items-center justify-center mb-4`} data-id="element-6133">
                    <layer.icon className="w-6 h-6 text-white" data-id="element-6134" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-1" data-id="element-6135">
                    {layer.title}
                  </h3>
                  <p className="text-sm text-slate-500 mb-4" data-id="element-6136">
                    {layer.subtitle}
                  </p>
                  <ul className="space-y-2 mb-4" data-id="element-6137">
                    {layer.items.map((item, j) => <li key={j} className="flex items-start gap-2 text-sm text-slate-700" data-id="element-6138">
                        <CheckCircle2Icon className="w-4 h-4 text-brand-teal shrink-0 mt-0.5" data-id="element-6139" />
                        <span data-id="element-6140">{item}</span>
                      </li>)}
                  </ul>
                  <div className="pt-4 border-t border-slate-100" data-id="element-6141">
                    <p className="text-xs text-slate-500 italic" data-id="element-6142">
                      {layer.note}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Content Layers */}
        <section className="py-20 px-6 md:px-12 border-b border-slate-100" data-id="element-6143">
          <div className="max-w-6xl mx-auto" data-id="element-6144">
            <div className="text-center mb-12" data-id="element-6145">
              <h2 className="font-serif-display text-4xl font-bold text-brand-navy mb-4" data-id="element-6146">
                Content Layers
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-6147">
                Separate canonical truth from generated outputs and delivery
                surfaces.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6" data-id="element-6148">
              {[{
              title: '1. Canonical Content',
              subtitle: 'Your "truth" layer',
              icon: ShieldCheckIcon,
              color: 'text-emerald-600',
              bg: 'bg-emerald-50',
              items: ['Retreat frameworks', 'Leadership modules', 'Facilitator guides', 'Case studies', 'Assessments', 'Pre-work questions', 'Reflection prompts', 'Workshop exercises', 'Client segment templates', 'Industry playbooks', 'Hotel/property content', 'Brand-approved media', 'Spanish/English translations'],
              storage: 'Lives in Sanity'
            }, {
              title: '2. Generated / Personalized',
              subtitle: 'AI-produced, client-specific',
              icon: SparklesIcon,
              color: 'text-purple-600',
              bg: 'bg-purple-50',
              items: ['Personalized learning path', 'Executive summary', 'Team capability map', 'Recommended modules', 'Post-retreat action plan', 'Individual reflection pack', 'Follow-up email sequence', 'Manager coaching guide', 'Board / leadership memo', 'Meeting summary', 'Role-specific next steps'],
              storage: 'Stored in Sanity, marked as generated & versioned'
            }, {
              title: '3. Delivery Surfaces',
              subtitle: 'Where users experience it',
              icon: LayersIcon,
              color: 'text-blue-600',
              bg: 'bg-blue-50',
              items: ['Client portal', 'Hotel hub', 'Corporate retreat microsite', 'PDF proposal', 'Post-retreat learning dashboard', 'Email drip sequence', 'Facilitator briefing page', 'Sales enablement page', 'Private link for executives'],
              storage: 'Fed by Sanity content'
            }].map((layer, i) => <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6" data-id="element-6149">
                  <div className={`w-10 h-10 rounded-lg ${layer.bg} flex items-center justify-center mb-4`} data-id="element-6150">
                    <layer.icon className={`w-5 h-5 ${layer.color}`} data-id="element-6151" />
                  </div>
                  <h3 className="text-lg font-bold text-brand-navy mb-1" data-id="element-6152">
                    {layer.title}
                  </h3>
                  <p className="text-sm text-slate-500 mb-4" data-id="element-6153">
                    {layer.subtitle}
                  </p>
                  <ul className="space-y-1.5 mb-4 text-sm text-slate-700" data-id="element-6154">
                    {layer.items.map((item, j) => <li key={j} className="flex items-start gap-2" data-id="element-6155">
                        <div className="w-1 h-1 rounded-full bg-slate-400 shrink-0 mt-2" data-id="element-6156" />
                        <span data-id="element-6157">{item}</span>
                      </li>)}
                  </ul>
                  <div className="pt-3 border-t border-slate-100" data-id="element-6158">
                    <p className="text-xs font-semibold text-slate-600" data-id="element-6159">
                      {layer.storage}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6" data-id="element-6160">
              <div className="flex items-start gap-3" data-id="element-6161">
                <AlertTriangleIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" data-id="element-6162" />
                <div data-id="element-6163">
                  <div className="font-bold text-amber-900 mb-1" data-id="element-6164">
                    Critical Rule
                  </div>
                  <p className="text-sm text-amber-800 leading-relaxed" data-id="element-6165">
                    Do NOT dump NotebookLM outputs into Sanity as blobs of text.
                    Break AI-generated content into structured objects (Client,
                    Cohort, Learning Goal, Action Item, etc.) so it becomes
                    operational, not just "more documents."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Video Layer · Mux */}
        <section id="video" className="py-20 px-6 md:px-12 bg-gradient-to-br from-pink-50/40 via-white to-white border-b border-slate-100 scroll-mt-32" data-id="element-6166">
          <div className="max-w-6xl mx-auto" data-id="element-6167">
            <div className="text-center mb-12" data-id="element-6168">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-100 text-pink-700 text-xs font-bold uppercase tracking-wider mb-4" data-id="element-6169">
                <span className="w-2 h-2 rounded-full bg-pink-500" data-id="element-6170" />
                Video Layer · Mux
              </div>
              <h2 className="font-serif-display text-4xl font-bold text-brand-navy mb-4" data-id="element-6171">
                Mux powers every video on the platform.
              </h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto" data-id="element-6172">
                Upload, encoding, adaptive streaming, thumbnails, and analytics.
                Mux is the video plugin behind speaker sessions, event recaps,
                attendee uploads, and the hotel's branded promo videos — across
                every event type.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10" data-id="element-6173">
              {[{
              title: 'Pre-event Promo',
              desc: 'Hotel-branded teaser videos on the event hub. Adaptive playback on any device.',
              icon: '🎬'
            }, {
              title: 'Speaker & Session Recordings',
              desc: 'Recorded keynotes, workshops, and panels — automatically encoded and chapter-marked.',
              icon: '🎤'
            }, {
              title: 'Live Streaming',
              desc: 'Premium conferences and hybrid events stream via Mux for remote attendees.',
              icon: '📡'
            }, {
              title: 'Attendee Uploads',
              desc: 'Guests drop in clips from weddings, birthdays, and team events — Mux handles transcode + thumbnails.',
              icon: '📱'
            }, {
              title: 'Post-event Recap Reels',
              desc: 'Auto-generated highlight reels stitched from event media and delivered via Mux.',
              icon: '✨'
            }, {
              title: 'Engagement Analytics',
              desc: 'Mux Data tracks views, watch time, completion — feeds the post-event report.',
              icon: '📊'
            }].map((f, i) => <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-pink-300 hover:shadow-md transition-all" data-id="element-6174">
                  <div className="text-2xl mb-3" data-id="element-6175">{f.icon}</div>
                  <h3 className="text-base font-bold text-brand-navy mb-2" data-id="element-6176">
                    {f.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-6177">
                    {f.desc}
                  </p>
                </div>)}
            </div>

            {/* How it wires in */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 max-w-5xl mx-auto" data-id="element-6178">
              <div className="text-xs font-bold text-pink-700 uppercase tracking-wider mb-4" data-id="element-6179">
                How Mux Wires Into the Stack
              </div>
              <div className="grid md:grid-cols-4 gap-3 items-stretch" data-id="element-6180">
                {[{
                step: '1',
                title: 'Upload',
                desc: 'Direct uploads from the Coordinator console or attendee hub via Mux Direct Upload URLs.'
              }, {
                step: '2',
                title: 'Encode',
                desc: 'Mux transcodes to adaptive bitrate (HLS) automatically — no infra to manage.'
              }, {
                step: '3',
                title: 'Store',
                desc: <>
                        Asset IDs and playback IDs stored on the{' '}
                        <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded" data-id="element-6181">
                          materials
                        </code>{' '}
                        table linked to events.
                      </>
              }, {
                step: '4',
                title: 'Stream',
                desc: 'Mux Player on the event hub. Analytics piped into the post-event report.'
              }].map((s, i) => <div key={i} className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-6182">
                    <div className="w-7 h-7 rounded-full bg-pink-500 text-white text-xs font-black flex items-center justify-center mb-3" data-id="element-6183">
                      {s.step}
                    </div>
                    <div className="text-sm font-bold text-brand-navy mb-1.5" data-id="element-6184">
                      {s.title}
                    </div>
                    <div className="text-xs text-slate-600 leading-relaxed" data-id="element-6185">
                      {s.desc}
                    </div>
                  </div>)}
              </div>

              <div className="mt-6 pt-6 border-t border-slate-100 grid sm:grid-cols-3 gap-4 text-xs" data-id="element-6186">
                <div data-id="element-6187">
                  <div className="font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-6188">
                    Schema impact
                  </div>
                  <div className="text-slate-700" data-id="element-6189">
                    Add{' '}
                    <code className="font-mono bg-slate-100 px-1 py-0.5 rounded" data-id="element-6190">
                      mux_asset_id
                    </code>{' '}
                    +{' '}
                    <code className="font-mono bg-slate-100 px-1 py-0.5 rounded" data-id="element-6191">
                      mux_playback_id
                    </code>{' '}
                    to the{' '}
                    <code className="font-mono bg-slate-100 px-1 py-0.5 rounded" data-id="element-6192">
                      materials
                    </code>{' '}
                    table.
                  </div>
                </div>
                <div data-id="element-6193">
                  <div className="font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-6194">
                    Package gating
                  </div>
                  <div className="text-slate-700" data-id="element-6195">
                    Live streaming locked to Smart Upgrade + Premium. Basic /
                    Lite get upload + playback only.
                  </div>
                </div>
                <div data-id="element-6196">
                  <div className="font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-6197">
                    Sprint slot
                  </div>
                  <div className="text-slate-700" data-id="element-6198">
                    Mux integration lands in Sprint 4 (W7–8) alongside the
                    Materials Library.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sanity Data Model */}
        <section className="py-20 px-6 md:px-12 bg-slate-50" data-id="element-6199">
          <div className="max-w-6xl mx-auto" data-id="element-6200">
            <div className="text-center mb-12" data-id="element-6201">
              <h2 className="font-serif-display text-4xl font-bold text-brand-navy mb-4" data-id="element-6202">
                Sanity Data Model
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-6203">
                Three object groups: Global canonical content, Hotel/brand
                assets, and Client personalization.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6" data-id="element-6204">
              {[{
              title: 'Global Objects',
              subtitle: 'Canonical content library',
              color: 'bg-brand-teal',
              objects: ['Retreat Program', 'Learning Module', 'Exercise', 'Framework', 'Assessment', 'Question Bank', 'Video', 'Article', 'Facilitator', 'Case Study', 'Media Asset', 'Industry Segment', 'Leadership Theme']
            }, {
              title: 'Hotel / Brand Objects',
              subtitle: 'Property & partner content',
              color: 'bg-brand-coral',
              objects: ['Brand Group', 'Property', 'Venue', 'Space', 'Package', 'Brand Hub', 'Co-Branded Landing Page', 'Sales Asset', 'Proposal Template']
            }, {
              title: 'Client / Personalization',
              subtitle: 'Generated & client-specific',
              color: 'bg-purple-600',
              objects: ['Client Organization', 'Cohort', 'Participant Profile', 'Learning Path', 'Personalized Recommendation', 'Session Output', 'NotebookLM Source Summary', 'Action Plan', 'Follow-Up Sequence', 'Progress Check-In']
            }].map((group, i) => <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6" data-id="element-6205">
                  <div className={`w-10 h-10 rounded-lg ${group.color} flex items-center justify-center mb-4`} data-id="element-6206">
                    <DatabaseIcon className="w-5 h-5 text-white" data-id="element-6207" />
                  </div>
                  <h3 className="text-lg font-bold text-brand-navy mb-1" data-id="element-6208">
                    {group.title}
                  </h3>
                  <p className="text-sm text-slate-500 mb-4" data-id="element-6209">
                    {group.subtitle}
                  </p>
                  <div className="space-y-1.5" data-id="element-6210">
                    {group.objects.map((obj, j) => <div key={j} className="text-sm font-mono text-slate-700 bg-slate-50 px-3 py-1.5 rounded border border-slate-100" data-id="element-6211">
                        {obj}
                      </div>)}
                  </div>
                </div>)}
            </div>
          </div>
        </section>

        {/* Workflow */}
        <section className="py-20 px-6 md:px-12 border-b border-slate-100" data-id="element-6212">
          <div className="max-w-5xl mx-auto" data-id="element-6213">
            <div className="text-center mb-12" data-id="element-6214">
              <h2 className="font-serif-display text-4xl font-bold text-brand-navy mb-4" data-id="element-6215">
                The Workflow
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-6216">
                From client diagnostic to post-retreat CRM tracking — a 7-step
                operational flow.
              </p>
            </div>

            <div className="space-y-4" data-id="element-6217">
              {[{
              step: '1',
              title: 'Client fills diagnostic',
              desc: 'Intake form captures goals, team composition, challenges, and desired outcomes.',
              icon: FileTextIcon
            }, {
              step: '2',
              title: 'NotebookLM / AI generates synthesis',
              desc: 'AI analyzes source material, client inputs, and canonical content to produce personalized recommendations.',
              icon: BrainCircuitIcon
            }, {
              step: '3',
              title: 'Human reviews and approves',
              desc: 'Facilitator or account manager verifies AI outputs, edits as needed, and approves for delivery.',
              icon: CheckCircle2Icon
            }, {
              step: '4',
              title: 'Sanity stores structured recommendations',
              desc: 'Approved content is saved as structured objects (Learning Path, Action Plan, etc.) in Sanity.',
              icon: DatabaseIcon
            }, {
              step: '5',
              title: 'Front-end delivers personalized learning path',
              desc: 'Client portal, participant hub, and hotel microsite pull from Sanity to display tailored content.',
              icon: MonitorIcon
            }, {
              step: '6',
              title: 'Facilitator uses same data to shape workshop',
              desc: 'Real-time access to participant profiles, pre-work responses, and session goals during the retreat.',
              icon: UsersIcon
            }, {
              step: '7',
              title: 'Post-retreat outputs become follow-up content',
              desc: 'Session notes, action plans, and progress check-ins feed into email sequences and CRM for next sale.',
              icon: TrendingUpIcon
            }].map((item, i) => <div key={i} className="flex items-start gap-4 bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow" data-id="element-6218">
                  <div className="w-10 h-10 rounded-lg bg-brand-teal/10 flex items-center justify-center shrink-0" data-id="element-6219">
                    <item.icon className="w-5 h-5 text-brand-teal" data-id="element-6220" />
                  </div>
                  <div className="flex-1" data-id="element-6221">
                    <div className="flex items-center gap-3 mb-2" data-id="element-6222">
                      <div className="w-6 h-6 rounded-full bg-brand-navy text-white text-xs font-bold flex items-center justify-center" data-id="element-6223">
                        {item.step}
                      </div>
                      <h3 className="text-lg font-bold text-brand-navy" data-id="element-6224">
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed" data-id="element-6225">
                      {item.desc}
                    </p>
                  </div>
                  {i < 6 && <ArrowRightIcon className="w-5 h-5 text-slate-300 shrink-0 mt-2" data-id="element-6226" />}
                </div>)}
            </div>
          </div>
        </section>

        {/* MVP Personalization Scope */}
        <section className="py-20 px-6 md:px-12 bg-slate-50" data-id="element-6227">
          <div className="max-w-5xl mx-auto" data-id="element-6228">
            <div className="text-center mb-12" data-id="element-6229">
              <h2 className="font-serif-display text-4xl font-bold text-brand-navy mb-4" data-id="element-6230">
                MVP Personalization Scope
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-6231">
                Start with three personalization dimensions. Don't try to
                personalize everything.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8" data-id="element-6232">
              {[{
              title: 'Role',
              icon: UsersIcon,
              color: 'bg-blue-500',
              items: ['CEO', 'Founder', 'VP Sales', 'HR Leader', 'Manager', 'Individual Contributor']
            }, {
              title: 'Goal',
              icon: TargetIcon,
              color: 'bg-emerald-500',
              items: ['Alignment', 'Strategy', 'Culture', 'Sales Kickoff', 'Leadership Reset', 'Team Building']
            }, {
              title: 'Stage',
              icon: CalendarIcon,
              color: 'bg-purple-500',
              items: ['Pre-retreat', 'During retreat', 'Post-retreat']
            }].map((dim, i) => <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6" data-id="element-6233">
                  <div className={`w-10 h-10 rounded-lg ${dim.color} flex items-center justify-center mb-4`} data-id="element-6234">
                    <dim.icon className="w-5 h-5 text-white" data-id="element-6235" />
                  </div>
                  <h3 className="text-lg font-bold text-brand-navy mb-4" data-id="element-6236">
                    {dim.title}
                  </h3>
                  <div className="space-y-2" data-id="element-6237">
                    {dim.items.map((item, j) => <div key={j} className="text-sm bg-slate-50 px-3 py-2 rounded border border-slate-100 text-slate-700" data-id="element-6238">
                        {item}
                      </div>)}
                  </div>
                </div>)}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-8" data-id="element-6239">
              <h3 className="text-xl font-bold text-brand-navy mb-6" data-id="element-6240">
                Example: VP Sales × Leadership Reset × Pre-retreat
              </h3>
              <div className="grid md:grid-cols-2 gap-6" data-id="element-6241">
                <div data-id="element-6242">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3" data-id="element-6243">
                    Personalized Content Delivered
                  </h4>
                  <ul className="space-y-2 text-sm text-slate-600" data-id="element-6244">
                    <li className="flex items-start gap-2" data-id="element-6245">
                      <CheckCircle2Icon className="w-4 h-4 text-brand-teal shrink-0 mt-0.5" data-id="element-6246" />
                      <span data-id="element-6247">
                        Pre-retreat diagnostic (sales-focused questions)
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-6248">
                      <CheckCircle2Icon className="w-4 h-4 text-brand-teal shrink-0 mt-0.5" data-id="element-6249" />
                      <span data-id="element-6250">
                        Three selected prep videos (leadership + sales)
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-6251">
                      <CheckCircle2Icon className="w-4 h-4 text-brand-teal shrink-0 mt-0.5" data-id="element-6252" />
                      <span data-id="element-6253">One reflection worksheet (team alignment)</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-6254">
                      <CheckCircle2Icon className="w-4 h-4 text-brand-teal shrink-0 mt-0.5" data-id="element-6255" />
                      <span data-id="element-6256">
                        Workshop modules matched to team issues (pipeline,
                        coaching)
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-6257">
                      <CheckCircle2Icon className="w-4 h-4 text-brand-teal shrink-0 mt-0.5" data-id="element-6258" />
                      <span data-id="element-6259">Post-retreat action plan (30-day sales goals)</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-6260">
                      <CheckCircle2Icon className="w-4 h-4 text-brand-teal shrink-0 mt-0.5" data-id="element-6261" />
                      <span data-id="element-6262">30-day follow-up learning path</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-6263">
                      <CheckCircle2Icon className="w-4 h-4 text-brand-teal shrink-0 mt-0.5" data-id="element-6264" />
                      <span data-id="element-6265">Manager discussion guide (1-on-1 coaching)</span>
                    </li>
                  </ul>
                </div>
                <div data-id="element-6266">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3" data-id="element-6267">
                    Powered By
                  </h4>
                  <div className="space-y-3" data-id="element-6268">
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-6269">
                      <div className="text-xs font-bold text-brand-teal uppercase tracking-wider mb-1" data-id="element-6270">
                        Canonical Content (Sanity)
                      </div>
                      <p className="text-sm text-slate-600" data-id="element-6271">
                        Leadership modules, sales frameworks, assessment
                        questions
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-6272">
                      <div className="text-xs font-bold text-purple-600 uppercase tracking-wider mb-1" data-id="element-6273">
                        AI Synthesis (NotebookLM)
                      </div>
                      <p className="text-sm text-slate-600" data-id="element-6274">
                        Personalized learning path, action plan, follow-up
                        sequence
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-6275">
                      <div className="text-xs font-bold text-brand-coral uppercase tracking-wider mb-1" data-id="element-6276">
                        Delivery (Offsite+)
                      </div>
                      <p className="text-sm text-slate-600" data-id="element-6277">
                        Participant hub, email drips, facilitator briefing
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Operating Principles */}
        <section className="py-20 px-6 md:px-12 border-b border-slate-100" data-id="element-6278">
          <div className="max-w-4xl mx-auto" data-id="element-6279">
            <div className="text-center mb-12" data-id="element-6280">
              <h2 className="font-serif-display text-4xl font-bold text-brand-navy mb-4" data-id="element-6281">
                Operating Principles
              </h2>
              <p className="text-lg text-slate-600" data-id="element-6282">
                The system only works if you are disciplined.
              </p>
            </div>

            <div className="bg-gradient-to-br from-brand-navy to-brand-navy/90 rounded-3xl p-10 md:p-12 text-white text-center" data-id="element-6283">
              <div className="font-serif-display text-3xl md:text-4xl font-bold mb-6 leading-tight" data-id="element-6284">
                AI can generate.
                <br data-id="element-6285" />
                Humans approve.
                <br data-id="element-6286" />
                Sanity structures.
                <br data-id="element-6287" />
                Offsite+ distributes.
              </div>
              <p className="text-lg text-slate-300" data-id="element-6288">
                That is the system. Follow it or risk content chaos.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mt-8" data-id="element-6289">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6" data-id="element-6290">
                <div className="flex items-start gap-3 mb-4" data-id="element-6291">
                  <CheckCircle2Icon className="w-5 h-5 text-emerald-600 shrink-0" data-id="element-6292" />
                  <h3 className="text-lg font-bold text-emerald-900" data-id="element-6293">
                    Do This
                  </h3>
                </div>
                <ul className="space-y-2 text-sm text-emerald-800" data-id="element-6294">
                  <li className="flex items-start gap-2" data-id="element-6295">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0 mt-1.5" data-id="element-6296" />
                    <span data-id="element-6297">Break AI outputs into structured Sanity objects</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-6298">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0 mt-1.5" data-id="element-6299" />
                    <span data-id="element-6300">Require human approval before publishing</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-6301">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0 mt-1.5" data-id="element-6302" />
                    <span data-id="element-6303">Version all generated content</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-6304">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0 mt-1.5" data-id="element-6305" />
                    <span data-id="element-6306">Mark client-specific vs. canonical clearly</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-6307">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0 mt-1.5" data-id="element-6308" />
                    <span data-id="element-6309">Start with 3 personalization dimensions (MVP)</span>
                  </li>
                </ul>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-xl p-6" data-id="element-6310">
                <div className="flex items-start gap-3 mb-4" data-id="element-6311">
                  <AlertTriangleIcon className="w-5 h-5 text-red-600 shrink-0" data-id="element-6312" />
                  <h3 className="text-lg font-bold text-red-900" data-id="element-6313">
                    Don't Do This
                  </h3>
                </div>
                <ul className="space-y-2 text-sm text-red-800" data-id="element-6314">
                  <li className="flex items-start gap-2" data-id="element-6315">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0 mt-1.5" data-id="element-6316" />
                    <span data-id="element-6317">Dump NotebookLM outputs as text blobs</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-6318">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0 mt-1.5" data-id="element-6319" />
                    <span data-id="element-6320">Let AI publish directly without review</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-6321">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0 mt-1.5" data-id="element-6322" />
                    <span data-id="element-6323">Try to personalize everything at once</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-6324">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0 mt-1.5" data-id="element-6325" />
                    <span data-id="element-6326">Use NotebookLM as your content database</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-6327">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0 mt-1.5" data-id="element-6328" />
                    <span data-id="element-6329">
                      Mix canonical and generated content without labels
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Tech Stack & Deployment */}
        <section className="py-20 px-6 md:px-12 bg-slate-50" data-id="element-6330">
          <div className="max-w-5xl mx-auto" data-id="element-6331">
            <div className="text-center mb-12" data-id="element-6332">
              <h2 className="font-serif-display text-4xl font-bold text-brand-navy mb-4" data-id="element-6333">
                Tech Stack & Deployment
              </h2>
              <p className="text-lg text-slate-600" data-id="element-6334">
                Next.js, Sanity, Supabase, Vercel — built with Claude Code and
                Cursor.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-6335">
              {[{
              title: 'Frontend',
              icon: CodeIcon,
              color: 'bg-blue-500',
              stack: [{
                name: 'Next.js 14',
                desc: 'App Router, Server Components'
              }, {
                name: 'React + TypeScript',
                desc: 'Type-safe component library'
              }, {
                name: 'Tailwind CSS',
                desc: 'Utility-first styling system'
              }, {
                name: 'Framer Motion',
                desc: 'Animation & interaction layer'
              }]
            }, {
              title: 'Backend & Data',
              icon: ServerIcon,
              color: 'bg-emerald-500',
              stack: [{
                name: 'Sanity CMS',
                desc: 'Structured content, real-time collaboration'
              }, {
                name: 'Supabase',
                desc: 'Auth, database, real-time subscriptions'
              }, {
                name: 'NotebookLM API',
                desc: 'AI synthesis & personalization'
              }, {
                name: 'Mux Video',
                desc: 'Upload, encoding, streaming, and analytics for all event video'
              }, {
                name: 'Vercel Edge Functions',
                desc: 'Serverless API routes'
              }]
            }, {
              title: 'Development Workflow',
              icon: WorkflowIcon,
              color: 'bg-purple-500',
              stack: [{
                name: 'Claude Code',
                desc: 'AI pair programming, architecture planning'
              }, {
                name: 'Cursor',
                desc: 'AI-native code editor'
              }, {
                name: 'GitHub',
                desc: 'Version control & CI/CD'
              }, {
                name: 'Vercel',
                desc: 'Preview deploys, production hosting'
              }]
            }, {
              title: 'Deployment',
              icon: CloudIcon,
              color: 'bg-orange-500',
              stack: [{
                name: 'Vercel',
                desc: 'Auto-deploy from main branch'
              }, {
                name: 'Sanity Studio',
                desc: 'Hosted CMS at /studio route'
              }, {
                name: 'Supabase Cloud',
                desc: 'Managed Postgres + Auth'
              }, {
                name: 'Cloudflare CDN',
                desc: 'Global edge caching'
              }]
            }].map((section, i) => <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6" data-id="element-6336">
                  <div className={`w-10 h-10 rounded-lg ${section.color} flex items-center justify-center mb-4`} data-id="element-6337">
                    <section.icon className="w-5 h-5 text-white" data-id="element-6338" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-4" data-id="element-6339">
                    {section.title}
                  </h3>
                  <div className="space-y-3" data-id="element-6340">
                    {section.stack.map((item, j) => <div key={j} data-id="element-6341">
                        <div className="text-sm font-bold text-slate-800" data-id="element-6342">
                          {item.name}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-6343">
                          {item.desc}
                        </div>
                      </div>)}
                  </div>
                </div>)}
            </div>
          </div>
        </section>

        {/* Risks & Guardrails */}
        <section className="py-20 px-6 md:px-12 border-b border-slate-100" data-id="element-6344">
          <div className="max-w-4xl mx-auto" data-id="element-6345">
            <div className="text-center mb-12" data-id="element-6346">
              <h2 className="font-serif-display text-4xl font-bold text-brand-navy mb-4" data-id="element-6347">
                Risks & Guardrails
              </h2>
              <p className="text-lg text-slate-600" data-id="element-6348">
                The danger is overbuilding too early and creating content chaos.
              </p>
            </div>

            <div className="space-y-6" data-id="element-6349">
              {[{
              risk: 'Content becomes a landfill of AI-generated documents',
              guardrail: 'Require human approval gates. Mark all generated content with metadata (source, version, approval status). Archive unused outputs after 90 days.'
            }, {
              risk: 'Personalization scope creeps beyond MVP',
              guardrail: 'Lock to 3 dimensions (Role × Goal × Stage) for first 6 months. No new personalization axes without product review.'
            }, {
              risk: 'Sanity becomes bloated with unstructured content',
              guardrail: 'Enforce strict schema validation. No freeform text fields over 500 characters. All content must map to a defined object type.'
            }, {
              risk: 'NotebookLM outputs bypass review and go live',
              guardrail: 'Build approval workflow into CMS. Generated content starts in "Draft" status, requires facilitator/admin approval to publish.'
            }, {
              risk: 'Multi-language content gets out of sync',
              guardrail: "Use Sanity's built-in localization. Lock translations to approved canonical versions. Flag outdated translations in CMS."
            }].map((item, i) => <div key={i} className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-6350">
                  <div className="flex items-start gap-4" data-id="element-6351">
                    <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center shrink-0" data-id="element-6352">
                      <AlertTriangleIcon className="w-4 h-4 text-red-600" data-id="element-6353" />
                    </div>
                    <div className="flex-1" data-id="element-6354">
                      <h3 className="text-base font-bold text-red-900 mb-2" data-id="element-6355">
                        Risk: {item.risk}
                      </h3>
                      <div className="flex items-start gap-3" data-id="element-6356">
                        <div className="w-6 h-6 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-6357">
                          <ShieldCheckIcon className="w-3.5 h-3.5 text-emerald-600" data-id="element-6358" />
                        </div>
                        <p className="text-sm text-slate-600 leading-relaxed" data-id="element-6359">
                          <span className="font-semibold text-emerald-900" data-id="element-6360">
                            Guardrail:
                          </span>{' '}
                          {item.guardrail}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="py-16 px-6 md:px-12 bg-gradient-to-br from-brand-navy to-brand-navy/90 text-white" data-id="element-6361">
          <div className="max-w-4xl mx-auto text-center" data-id="element-6362">
            <h2 className="font-serif-display text-3xl md:text-4xl font-bold mb-6" data-id="element-6363">
              Ready to build the future of executive learning?
            </h2>
            <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto" data-id="element-6364">
              This PRD is a living document. As the product evolves, so will the
              architecture.
            </p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-brand-coral text-white rounded-xl font-bold hover:bg-brand-coral/90 transition-colors" data-id="element-6365">
              Back to Home
              <ArrowRightIcon className="w-5 h-5" data-id="element-6366" />
            </Link>
          </div>
        </section>
      </div>
      <RoleWorkflowModal roleId={activeRoleId} onClose={() => setActiveRoleId(null)} data-id="element-6367" />
    </>;
}